import { getBootstrapData } from "@swa-ui/bootstrap";

/**
 * getStations
 *
 * Return value: array of all station info. Can provide override bootstrap key.
 */

export const getStations = (options = {}) => {
  const { bootstrapKey = "air/all-air-stations" } = options;

  return getBootstrapData(bootstrapKey) ?? getBootstrapData("air/stations-data");
};

/**
 * getStation
 *
 * Parameter Example: 'DAL'
 * Return value: Station info for given ID. 'undefined' will be return if ID is invalid.
 */

export const getStation = (stationCode, options) =>
  getStations(options).find((stationInfo) => stationInfo.id === stationCode);

/**
 * getInternationalStationCode
 *
 * Return value: Array of all international station ID.
 */

export const getInternationalStationCode = (options) => {
  const internationalStations = getStations(options).filter(
    (station) => station?.internationalStation === true
  );

  return internationalStations.map((internationalStationCode) => internationalStationCode?.id);
};

/**
 * getStationNameForStation accepts a station object and outputs the
 * display name.
 *
 * Parameter example: {
 *  airportGroup: false,
 *  displayName: "Chicago (Midway), IL",
 *  id: "MDW"
 * }
 * Return Value: "Chicago (Midway), IL"
 */
export const getStationNameForStation = (station) =>
  station && !station.isAirportGroup ? station.displayName : "";

/**
 * getStationName accepts an airport code and outputs the display name.
 *
 * Parameter example: 'ABQ'
 * Return value: Albuquerque, NM - ABQ
 *
 */
export const getStationName = (stationCode, options) => {
  const station = getStation(stationCode, options);

  return getStationNameForStation(station);
};

/**
 * getStationNameAndState outputs the station name combined with the state federal unit.
 *
 * @param {string} stationCode
 * @returns {string}
 *
 */

export const getStationNameAndState = (stationCode, options) => {
  const station = getStation(stationCode, options);
  let stationNameAndState = "";

  if (station) {
    const { stateFederalUnit, stationName } = station;

    stationNameAndState = stationName;

    if (stateFederalUnit?.length > 0) {
      stationNameAndState = `${stationNameAndState}, ${stateFederalUnit}`;
    }
  }

  return stationNameAndState;
};

/**
 * getShortDisplayStationName outputs the display name from a given airport code.
 *
 * Parameter example: 'ABQ'
 * Return value: Albuquerque
 *
 */

export const getShortDisplayStationName = (stationCode, options) => {
  const station = getStation(stationCode, options);

  return station && !station.isAirportGroup ? station.shortDisplayName : "";
};

/**
 * getStationForName returns the station info for the given station display name.
 *
 * Parameter example: 'Albuquerque, NM - ABQ'
 * Return value: station info
 *
 */
export const getStationForName = (displayName, options) =>
  getStations(options)?.find((station) => station.displayName === displayName);

/**
 * getAreaName returns the station info for the given airport group id.
 *
 * Parameter example: 'SFC'
 * Return value: station group info
 *
 */
export function getAreaName(stationId, options) {
  const station = getStation(stationId, options);

  return station?.isAirportGroup ? station?.displayName : undefined;
}

/**
 * getStationOrAreaName returns the station info for the given station id. StationId can be a
 * reference to an airport group or a typical station.
 *
 * Parameter example: 'ABQ'
 * Return value: station info
 *
 */
export function getStationOrAreaName(stationId, options) {
  const station = getStation(stationId, options);

  return station ? station?.displayName : "";
}
